/**
 * Website field module — modular-form replacement for the legacy website field.
 *
 * Replaces:
 *  • Schema → packages/frontend/src/schema/workspaceSchema.ts:166-188
 *      website: optional, URL test with implied https://, "Website is not valid"
 *  • UI → ContactDetailsForm.tsx InputFieldV2 (label "Website")
 */
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@sr/ui-library';
import { Control } from 'react-hook-form';
import * as yup from 'yup';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsFormValues } from '../../shared/types';

interface WebsiteFieldProps {
  control: Control<WorkspaceDetailsFormValues>;
}

/** Optional website URL — a protocol is implied when the user omits one. */
const WebsiteFieldComponent = ({ control }: WebsiteFieldProps) => (
  <FormField
    name={WORKSPACE_DETAILS_FORM_FIELDS.website}
    control={control}
    render={({ field }) => (
      <FormItem>
        <FormLabel>Website</FormLabel>
        <FormControl>
          <Input {...field} value={field.value ?? ''} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

const urlSchema = yup.string().url();

// Settings-page schema. The legacy onboarding form labels this field
// "Website or LinkedIn" — when onboarding adopts the modular form, add a
// separate onboarding schema/label alongside (see EntityNameField pattern).
const schema = yup
  .string()
  .nullable()
  .test('validator-website', 'Website is not valid', (value) => {
    if (value == null || value === '') return true;

    // Add protocol if missing — mirrors the legacy validator.
    const urlWithProtocol =
      value.startsWith('http://') || value.startsWith('https://')
        ? value
        : `https://${value}`;

    return urlSchema.isValidSync(urlWithProtocol);
  });

type WebsiteFieldStatics = {
  schema: yup.AnySchema;
};

type WebsiteFieldWithStatics = typeof WebsiteFieldComponent &
  WebsiteFieldStatics;

export const WebsiteField = Object.assign(WebsiteFieldComponent, {
  schema,
}) as WebsiteFieldWithStatics;
